import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Upload, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const empty = {
  medicine_name: "", generic_name: "", brand_name: "", strength: "", dosage_form: "",
  default_dosage: "", default_frequency: "", default_duration: "", default_instruction: "", category: "",
  unit_price: "", stock_quantity: "", unit: "", expiry_date: "", low_stock_threshold: 10,
};

export default function Medicines() {
  // Archiving hits the SHARED medicine master — it hides the medicine from every
  // clinic — so the backend restricts it to admins. Reflect that in the UI rather
  // than handing everyone a button that 403s.
  const { user } = useAuth();
  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.role];
  const canArchive = roles.some((r) => r === "admin" || r === "hospital_admin");

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const load = async (search = "") => {
    const { data } = await api.get(`/medicines${search ? `?q=${encodeURIComponent(search)}` : ""}`);
    setItems(Array.isArray(data) ? data : []);
    setSelected(new Set());
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]);

  const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (m) => {
    setForm({
      medicine_name: m.medicine_name || "",
      generic_name: m.generic_name || "",
      brand_name: m.brand_name || "",
      strength: m.strength || "",
      dosage_form: m.dosage_form || "",
      default_dosage: m.default_dosage || "",
      default_frequency: m.default_frequency || "",
      default_duration: m.default_duration || "",
      default_instruction: m.default_instruction || "",
      category: m.category || "",
      unit_price: m.unit_price ?? "",
      stock_quantity: m.stock_quantity ?? "",
      unit: m.unit || "",
      expiry_date: m.expiry_date || "",
      low_stock_threshold: m.low_stock_threshold ?? 10,
    });
    setEditingId(m.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        unit_price: form.unit_price === "" ? 0 : Number(form.unit_price),
        stock_quantity: form.stock_quantity === "" ? 0 : Number(form.stock_quantity),
        low_stock_threshold: Number(form.low_stock_threshold) || 10,
      };
      if (editingId) {
        await api.put(`/medicines/${editingId}`, payload);
        toast.success("Medicine updated");
      } else {
        await api.post("/medicines", payload);
        toast.success("Medicine added");
      }
      setOpen(false);
      load(q);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm(
      "Archive this medicine?\n\nThe medicine catalog is shared, so this hides it from every clinic."
    )) return;
    // This used to have no error handling: a 403 for non-admins threw an
    // unhandled rejection, so the button looked like it simply did nothing.
    try {
      await api.delete(`/medicines/${id}`);
      toast.success("Medicine archived");
      load(q);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not archive medicine");
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((m) => m.id)));
  };

  const bulkDelete = async (permanent = false) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const verb = permanent ? "PERMANENTLY DELETE" : "archive";
    if (!window.confirm(`${verb} ${ids.length} medicine${ids.length === 1 ? "" : "s"}?${permanent ? " This cannot be undone." : ""}`)) return;
    try {
      const { data } = await api.post("/medicines/bulk-delete", { ids, permanent });
      toast.success(`${permanent ? "Deleted" : "Archived"} ${data.deleted ?? data.archived ?? ids.length} medicine${ids.length === 1 ? "" : "s"}`);
      load(q);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && selected.size < items.length;

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="medicines-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Medicines</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} record{items.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="btn-bulk-upload-medicines">
            <Link to="/medicines/bulk-upload"><Upload size={14} className="mr-2" /> Bulk Upload</Link>
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-medicine">
            <Plus size={14} className="mr-2" /> Add Medicine
          </Button>
        </div>
      </div>

      <Card className="p-4 border-slate-200 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, brand, generic..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="medicine-search" />
        </div>
      </Card>

      {selected.size > 0 ? (
        <Card className="p-3 border-blue-300 bg-blue-50 mb-3 flex items-center justify-between flex-wrap gap-2" data-testid="bulk-action-bar">
          <div className="text-sm text-blue-900 flex items-center gap-2">
            <span className="font-semibold" data-testid="bulk-selected-count">{selected.size}</span> selected
            <button onClick={() => setSelected(new Set())} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">
              <X size={12} /> Clear
            </button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => bulkDelete(false)} data-testid="btn-bulk-archive">
              <Trash2 size={14} className="mr-1" /> Archive Selected
            </Button>
            {canArchive ? (
              <Button size="sm" variant="outline" onClick={() => bulkDelete(true)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-bulk-delete">
                <Trash2 size={14} className="mr-1" /> Permanently Delete
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    data-testid="select-all-medicines"
                    className="rounded border-slate-300"
                  />
                </th>
                <th className="px-4 py-3 text-left font-semibold">Medicine</th>
                <th className="px-4 py-3 text-left font-semibold">Strength / Form</th>
                <th className="px-4 py-3 text-right font-semibold">Price</th>
                <th className="px-4 py-3 text-right font-semibold">Stock</th>
                <th className="px-4 py-3 text-right font-semibold">Value</th>
                <th className="px-4 py-3 text-left font-semibold">Expiry</th>
                <th className="px-4 py-3 text-left font-semibold">Category</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-8 text-center text-slate-400">No medicines yet.</td></tr>
              ) : items.map((m) => {
                const price = Number(m.unit_price || 0);
                const qty = Number(m.stock_quantity || 0);
                const value = price * qty;
                const threshold = Number(m.low_stock_threshold || 10);
                const lowStock = qty <= threshold;
                let expClass = "text-slate-700";
                let expHint = "";
                if (m.expiry_date) {
                  const exp = new Date(m.expiry_date);
                  if (!isNaN(exp.getTime())) {
                    const days = Math.floor((exp - new Date()) / 86400000);
                    if (days < 0) { expClass = "text-red-700 font-semibold"; expHint = "expired"; }
                    else if (days <= 30) { expClass = "text-red-600 font-medium"; expHint = `${days}d`; }
                    else if (days <= 180) { expClass = "text-amber-600"; expHint = `${days}d`; }
                  }
                }
                return (
                  <tr key={m.id} className={`border-t border-slate-100 hover:bg-slate-50 ${selected.has(m.id) ? "bg-blue-50/40" : ""}`} data-testid={`medicine-row-${m.id}`}>
                    <td className="px-3 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={selected.has(m.id)}
                        onChange={() => toggleOne(m.id)}
                        data-testid={`select-medicine-${m.id}`}
                        className="rounded border-slate-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{m.medicine_name}</div>
                      <div className="text-xs text-slate-500">{m.generic_name}{m.brand_name ? ` · ${m.brand_name}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      <div>{m.strength || "—"}</div>
                      <div className="text-slate-500">{m.dosage_form || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-right mono text-xs">₱{price.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right mono text-xs ${lowStock ? "text-red-600 font-semibold" : "text-slate-700"}`}>
                      {qty}{m.unit ? ` ${m.unit}` : ""}
                      {lowStock && qty > 0 ? <span className="block text-[9px] uppercase tracking-wider">low</span> : null}
                      {qty === 0 ? <span className="block text-[9px] uppercase tracking-wider">out</span> : null}
                    </td>
                    <td className="px-4 py-3 text-right mono text-xs text-slate-700">₱{value.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-xs ${expClass}`}>
                      {m.expiry_date || "—"}
                      {expHint ? <span className="block text-[9px] uppercase tracking-wider">{expHint}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{m.category || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`medicine-edit-${m.id}`}><Pencil size={14} /></Button>
                      {canArchive ? <Button size="sm" variant="ghost" onClick={() => del(m.id)} data-testid={`medicine-delete-${m.id}`} title="Archive from the shared catalog"><Trash2 size={14} className="text-red-600" /></Button> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="medicine-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Medicine" : "Add Medicine"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Medicine Name *</Label>
              <Input value={form.medicine_name} onChange={(e) => setForm({ ...form, medicine_name: e.target.value })} data-testid="medicine-input-name" />
            </div>
            <div><Label>Generic Name</Label><Input value={form.generic_name} onChange={(e) => setForm({ ...form, generic_name: e.target.value })} /></div>
            <div><Label>Brand Name</Label><Input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} /></div>
            <div><Label>Strength</Label><Input value={form.strength} onChange={(e) => setForm({ ...form, strength: e.target.value })} placeholder="500mg" /></div>
            <div><Label>Dosage Form</Label><Input value={form.dosage_form} onChange={(e) => setForm({ ...form, dosage_form: e.target.value })} placeholder="Tablet, Capsule, ..." /></div>
            <div><Label>Default Dosage</Label><Input value={form.default_dosage} onChange={(e) => setForm({ ...form, default_dosage: e.target.value })} placeholder="1 tablet" /></div>
            <div><Label>Default Frequency</Label><Input value={form.default_frequency} onChange={(e) => setForm({ ...form, default_frequency: e.target.value })} /></div>
            <div><Label>Default Duration</Label><Input value={form.default_duration} onChange={(e) => setForm({ ...form, default_duration: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Inventory</Label>
            </div>
            <div><Label>Unit Price (₱)</Label><Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="0.00" data-testid="medicine-input-price" /></div>
            <div><Label>Stock Quantity</Label><Input type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} placeholder="0" data-testid="medicine-input-stock" /></div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="tablet, capsule, bottle..." data-testid="medicine-input-unit" /></div>
            <div><Label>Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="medicine-input-expiry" /></div>
            <div><Label>Low-stock Threshold</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Default Instruction</Label>
              <Input value={form.default_instruction} onChange={(e) => setForm({ ...form, default_instruction: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="medicine-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
